import type { QuestionProps } from "../../../components/Questionnaire/Section/Question/types";
import type { VisibilityMap } from "../../../components/Questionnaire/validation/form/types";
import type { AddressModes, Groups, Section } from "../types";
import { isQuestionInGroup } from "../../../components/Questionnaire/Section/Question/types";
import { shallowEqual } from "../../../utils/shallowEqual";

type GetVisibilityMapArgs = {
  sections: Section[];
  addressModes: AddressModes;
  groups: Groups;
  visibility: VisibilityMap;
  groupQuestionNames?: Set<string>;
};

// This helper function is used to get a map of question names to visibile status.
// It is used in setAction and mergeAction
export const getVisibilityMap = ({
  sections,
  addressModes,
  groups,
  visibility,
  groupQuestionNames,
}: GetVisibilityMapArgs): Record<string, boolean> => {
  const newVisibilityMap: Record<string, boolean> = {};

  // Use Set for O(1) lookup if provided, otherwise fall back to O(n) isQuestionInGroup
  const isInGroup = groupQuestionNames
    ? (name: string) => groupQuestionNames.has(name)
    : (name: string) => isQuestionInGroup(name, groups);

  for (const section of sections) {
    for (const q of section.questions as QuestionProps[]) {
      if (!q.name) continue;

      // If the question is part of a group, prefer existing visibility state
      if (isInGroup(q.name)) {
        newVisibilityMap[q.name] = visibility?.[q.name] ?? false;
      } else if (
        // If the question is associated with an address lookup and lookup mode is active, hide it
        q.associatedQuestionName &&
        addressModes[q.associatedQuestionName] === "lookup"
      ) {
        newVisibilityMap[q.name] = false;
      } else {
        newVisibilityMap[q.name] = q.visible ?? q.suggested ?? false;
      }
    }
  }

  // Preserve existing visibility reference if shallowly equal to prevent unnecessary re-renders
  return visibility && shallowEqual(visibility, newVisibilityMap)
    ? visibility
    : newVisibilityMap;
};
